/**
 * <s-load-more> — appends the next page of products to the grid instead of navigating.
 * Without JavaScript the inner <a> is a normal link to the next page.
 *
 * Attributes:
 *   grid           — id of the list to append items to (e.g. "product-grid")
 *   loading-label  — button text while fetching
 *   loaded-message — announced after items are added; "{count}" is replaced with the number shown
 *
 * Children:
 *   a[data-load-more-link] — href is the next page URL (Liquid paginate.next.url)
 *   [data-load-more-count] — optional element whose text is replaced by the next page's count text
 */
class SLoadMore extends HTMLElement {
  #ac;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #onClick(event) {
    const link = event.target.closest('a[data-load-more-link]');
    if (!link || !this.contains(link)) return;
    event.preventDefault();
    if (this.#busy) return;

    const grid = document.getElementById(this.getAttribute('grid') || '');
    if (!grid) {
      window.location.href = link.href;
      return;
    }

    this.#busy = true;
    const originalLabel = link.textContent;
    link.setAttribute('aria-busy', 'true');
    link.textContent = this.getAttribute('loading-label') || originalLabel;

    try {
      const response = await fetch(link.href, { signal: this.#ac.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = doc.getElementById(grid.id);
      const items = nextGrid ? Array.from(nextGrid.children) : [];
      const firstNew = items[0];

      for (const item of items) grid.append(document.adoptNode(item));

      const nextLoadMore = doc.querySelector('s-load-more');
      const nextLink = nextLoadMore?.querySelector('a[data-load-more-link]');
      const count = this.querySelector('[data-load-more-count]');
      const nextCount = nextLoadMore?.querySelector('[data-load-more-count]');
      if (count && nextCount) count.textContent = nextCount.textContent;

      if (nextLink) {
        link.href = nextLink.href;
        link.textContent = originalLabel;
        link.removeAttribute('aria-busy');
      } else {
        link.remove();
      }

      const message = this.getAttribute('loaded-message');
      const status = this.querySelector('[data-load-more-status]');
      if (message && status) status.textContent = message.replace('{count}', String(grid.children.length));

      // Move keyboard focus to the first new product so keyboard users continue where they left off.
      firstNew?.querySelector('a')?.focus({ preventScroll: true });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      window.location.href = link.href;
    } finally {
      this.#busy = false;
    }
  }
}

if (!customElements.get('s-load-more')) customElements.define('s-load-more', SLoadMore);
